import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toOptions } from '@/shared/lib/options';
import { getListRows, getTotalPages } from '@/shared/lib/pagination';
import { toSearchParams } from '@/shared/lib/search';
import { useListFilters } from '@/shared/lib/useListFilters';
import { FilterActions } from '@/shared/ui/FilterActions';
import { FilterField } from '@/shared/ui/FilterField';
import { FilterSelect } from '@/shared/ui/FilterSelect';
import { Pagination } from '@/shared/ui/Pagination';
import { QueryStatus } from '@/shared/ui/QueryStatus';
import { SearchKeyInput } from '@/shared/ui/SearchKeyInput';
import { SortableTh } from '@/shared/ui/SortableTh';
import { fetchCarMarks, fetchSeoModels, type SeoModel } from './api';
import { OnlySeoActiveFilter, SitemapToggleCell } from './SitemapToggle';

const LIST_KEY = 'seo-models';

const SEARCH_KEY_OPTIONS = [
  { value: 'id', label: 'Поиск по id' },
  { value: 'name', label: 'Поиск по модели' },
];

const emptyFilters = { searchKey: '', searchValue: '', markId: '' };

export const SeoModelsPage = () => {
  const list = useListFilters(emptyFilters);
  const { draft, applied, page, sortField, sortOrder } = list;
  const [onlyActive, setOnlyActive] = useState(false);

  const marks = useQuery({ queryKey: ['car-marks'], queryFn: fetchCarMarks });

  const params = {
    page,
    sort_field: sortField,
    sort_order: sortOrder,
    ...toSearchParams(applied.searchKey, applied.searchValue),
    mark_id: applied.markId,
    is_seo_active: onlyActive ? (1 as const) : undefined,
  };
  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: [LIST_KEY, params],
    queryFn: () => fetchSeoModels(params),
  });
  const rows = getListRows<SeoModel>(data, ['models']);

  return (
    <>
      <section className="content-header">
        <h1>Модели</h1>
      </section>
      <section className="content">
        <div className="box">
          <div className="box-header with-border">
            <form onSubmit={list.submit}>
              <FilterField label="Поиск">
                <SearchKeyInput
                  keyOptions={SEARCH_KEY_OPTIONS}
                  searchKey={draft.searchKey}
                  searchValue={draft.searchValue}
                  onKeyChange={(v) => list.setField('searchKey', v)}
                  onValueChange={(v) => list.setField('searchValue', v)}
                />
              </FilterField>
              <FilterField label="Фильтрация по марке">
                <FilterSelect
                  options={toOptions(marks.data)}
                  value={draft.markId}
                  isLoading={marks.isLoading}
                  onChange={(v) => list.setField('markId', v)}
                />
              </FilterField>
              <FilterActions disabled={isFetching} onReset={list.reset} />
            </form>
          </div>
          <div className="box-header with-border">
            <OnlySeoActiveFilter
              checked={onlyActive}
              onChange={(checked) => {
                setOnlyActive(checked);
                list.setPage(1);
              }}
            />
          </div>
          <div className="box-body">
            <QueryStatus isLoading={isLoading} error={error} errorText="Ошибка загрузки моделей" />
            <table className="table table-bordered">
              <thead>
                <tr>
                  <SortableTh active={sortField === 'id'} onClick={() => list.toggleSort('id')}>
                    id
                  </SortableTh>
                  <SortableTh active={sortField === 'name'} onClick={() => list.toggleSort('name')}>
                    Модель
                  </SortableTh>
                  <SortableTh
                    active={sortField === 'mark_id'}
                    onClick={() => list.toggleSort('mark_id')}
                  >
                    Марка
                  </SortableTh>
                  <th>Участвует в структуре сайта</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((model) => (
                  <tr key={model.id}>
                    <td>{model.id}</td>
                    <td>{model.name}</td>
                    <td>{model.mark || 'No mark name'}</td>
                    <td>
                      <SitemapToggleCell
                        entity="model"
                        id={model.id}
                        isActive={Boolean(model.is_seo_active)}
                        listKey={LIST_KEY}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination page={page} totalPages={getTotalPages(data)} onChange={list.setPage} />
          </div>
        </div>
      </section>
    </>
  );
};
